import React, { useState, useCallback, useEffect } from "react";
import { Flex, VStack, Box, Text, Button, IconButton } from "@chakra-ui/react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

// Nudges the user to bind a GitHub repository to this plan once the GitHub
// connector is connected but no active binding exists yet. Shared between the
// chat footer and the Jobs panel's product requirement detail view.
export default function GitHubConnectBanner({ planId, dismissible = false }) {
  const [needsRepository, setNeedsRepository] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const refreshBindingStatus = useCallback(async () => {
    if (!planId) {
      setNeedsRepository(false);
      return;
    }
    try {
      const connectorsRes = await fetchWithAuth("/api/connectors");
      if (!connectorsRes?.ok) return;
      const connectorsData = await connectorsRes.json();
      const github = connectorsData.connectors?.find((connector) => connector.id === "github");
      if (github?.status !== "connected") {
        setNeedsRepository(false);
        return;
      }
      const bindingsRes = await fetchWithAuth(`/api/connectors/plans/${planId}/bindings`);
      if (!bindingsRes?.ok) return;
      const bindingsData = await bindingsRes.json();
      const hasGitHubBinding = (bindingsData.bindings || []).some(
        (binding) => binding.connector_id === "github" && binding.is_active !== false
      );
      setNeedsRepository(!hasGitHubBinding);
    } catch {
      // Setup guidance is non-critical; avoid disrupting the surrounding UI.
    }
  }, [planId]);

  useEffect(() => {
    refreshBindingStatus();
    const refresh = () => refreshBindingStatus();
    window.addEventListener("focus", refresh);
    window.addEventListener("pabs:githubBindingsUpdated", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pabs:githubBindingsUpdated", refresh);
    };
  }, [refreshBindingStatus]);

  if (!needsRepository || dismissed) return null;

  return (
    <Flex
      align="flex-start"
      gap={2}
      px={3}
      py={2}
      mb={2}
      bg="orange.50"
      border="1px solid"
      borderColor="orange.200"
      borderRadius="xl"
    >
      <Box as="span" className="material-icons-outlined" fontSize="16px" color="orange.500" mt="1px">
        account_tree
      </Box>
      <VStack align="stretch" spacing={2} flex={1} minW={0}>
        <Text fontSize="xs" color="orange.900" lineHeight="1.4">
          GitHub is connected. Choose a repository so Pabs can use delivery evidence for this plan.
        </Text>
        <Button
          size="xs"
          alignSelf="flex-start"
          colorScheme="orange"
          variant="outline"
          onClick={() => window.dispatchEvent(new CustomEvent("pabs:openGitHubRepositories", {
            detail: { planId },
          }))}
        >
          Choose repository
        </Button>
      </VStack>
      {dismissible && (
        <IconButton
          size="xs"
          flexShrink={0}
          variant="ghost"
          colorScheme="orange"
          aria-label="Dismiss"
          icon={<Box as="span" className="material-icons-outlined" fontSize="14px">close</Box>}
          onClick={() => setDismissed(true)}
        />
      )}
    </Flex>
  );
}
